import { Button, Form, Input, Select, Table } from "antd";
import Icon from "../../components/Icon";
import HrnModal from "../../components/HrnModal";
import validateMessages, { requiredRule } from "../../utils/validate";
import { LoadingOutlined } from "@ant-design/icons";
import { Actions, SortingHandler } from "../../shared/constants";
import { useEffect, useState } from "react";
import globalUtils from "../../utils/globalUtils";
import tableUtils from "../../utils/tableUtils";
import { LocationsColumn } from "../../shared/table/columns";
import { useCreateLocationMutation, useDeleteLocationMutation, useGetLocationsQuery, useLazyGetLocationsQuery, useUpdateLocationMutation } from "../../services/location";
import hrnUtils from "../../utils/hrnUtils";

const Location = () => {
    const [locationForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [type, setType] = useState<string | null>(null);
    const [srcType, setSrcType] = useState<string | null>(null);
    const [parents, setParents] = useState<any[]>([]);
    const [parentLabel, setParentLabel] = useState<string>("Parent");
    const [srcParentId, setSrcParentId] = useState<number | null | any>(null);

    const [getLocations, getLocationsResult] = useLazyGetLocationsQuery();
    const [createLocation, createLocationResult] = useCreateLocationMutation();
    const [updateLocation, updateLocationResult] = useUpdateLocationMutation();
    const [deleteLocation] = useDeleteLocationMutation();
    const [getParents, getParentsResult] = useLazyGetLocationsQuery();
    useEffect(() => {
        getLocations({ type: srcType, parentId: srcParentId <= 0 ? null : srcParentId }).unwrap();
    }, [srcType, srcParentId]);

    useEffect(() => {
        if (getLocationsResult.isSuccess) {
            setDataSource(getLocationsResult.data || []);
        }
    }, [getLocationsResult.isFetching]);

    const onLocationTypeChanged = async (value: string, source: "modal" | "filter") => {
        if (source === "modal") {
            setType(value);
        } else {
            setSrcType(value);
        }
        switch (value?.toLowerCase()) {
            case "country":
                setParents([{ id: -1, name: "<NotNeeded>" }]);
                setParentLabel("Parent");
                break;
            case "division":
                setParents([]);
                setParentLabel("Country");
                await getParents({ type: "country" }).unwrap();
                break;
            case "district":
                setParents([]);
                await getParents({ type: "division" }).unwrap();
                setParentLabel("Division");
                break;
            case "thana":
                setParents([]);
                await getParents({ type: "district" }).unwrap();
                setParentLabel("District");
                break;
            case "area":
                setParents([]);
                await getParents({ type: "thana" }).unwrap();
                setParentLabel("Thana");
                break;
            default:
                setParents([]);
                setParentLabel("Parent");
                break;
        }
    };

    useEffect(() => {
        if (getParentsResult.isSuccess) {
            setParents(getParentsResult.data || []);
        }
    }, [getParentsResult.isFetching]);

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
        locationForm.setFieldsValue({type: srcType, parentId: srcParentId});
        onLocationTypeChanged(srcType || "Country", "modal");
    };

    const hideModal = () => {
        setOpen(false);
        locationForm.resetFields();
        setMode("");
    };

    const onSave = async () => {
        try {
            await locationForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = locationForm.getFieldsValue();
        const payloadBody = { ...formFields, parentId: formFields.parentId <= 0 ? null : formFields.parentId };
        if (mode === Actions.CREATE) {
            // ** try catch must need for mutation!
            try {
                await createLocation(payloadBody).unwrap();
                setOpen(false);
                locationForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateLocation({ id: editId, body: payloadBody }).unwrap();
                setOpen(false);
                locationForm.resetFields();
            } catch (e) { }
        }
    };

    const handleEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        locationForm.setFieldsValue(record);
        onLocationTypeChanged(record.type, "modal");
    };

    const handleDelete = async (record: any) => {
        try {
            await deleteLocation({ id: record.id }).unwrap();
        } catch (e) { }
    };


    return (
        <>
            <div className="flex justify-between items-center gap-3 pb-6">
                <div className="text-headline">{"Locations"}</div>
                <Button type="primary" size="large" className="flex-end" onClick={showModal}>
                    <div className="flex items-center gap-1.5">
                        <Icon icon="add" className="h-4.5 w-4.5" />
                        <div>{"Add Location"}</div>
                    </div>
                </Button>
            </div>
            <div className="flex justify-between flex-wrap items-center gap-4 pb-4">
                <div className="flex items-center gap-x-4">
                    <div className="font-medium">
                        {dataSource?.length}{" "}
                        {globalUtils.checkIsSingular(dataSource.length)
                            ? "Location"
                            : "Locations"}
                    </div>
                </div>

                <div className="flex items-center gap-x-4">
                    <span>Type</span>
                    <Select
                        className="w-[200px]"
                        placeholder={"Type"}
                        options={
                            ["Country", "Division", "District", "Thana", "Area"].map(x => ({
                                label: x,
                                value: x,
                            }))
                        }
                        onChange={(value) => onLocationTypeChanged(value, "filter")}
                        allowClear
                    />
                    <span>{parentLabel}</span>
                    <Select
                        className="w-[400px]"
                        placeholder={parentLabel}
                        filterOption={hrnUtils.dropdownFilterOption}
                        options={
                            parents.map((x: any) => ({
                                label: x.name,
                                value: x.id,
                            }))
                        }
                        onChange={(value) => setSrcParentId(value)}
                        allowClear
                        showSearch
                    />
                </div>
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Location`}
                    width={520}
                    isLoading={
                        (createLocationResult.isLoading ||
                            updateLocationResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={locationForm}
                            layout="vertical"
                            name="locationForm"
                            validateMessages={validateMessages}
                        >
                            <Form.Item name="name" label={<>Name<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                <Input size="large" placeholder={"Name"} maxLength={30} />
                            </Form.Item>
                            <Form.Item name="type" label={<>Type<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                <Select
                                    className="w-full"
                                    placeholder={"Type"}
                                    options={
                                        ["Country", "Division", "District", "Thana", "Area"].map(x => ({
                                            label: x,
                                            value: x,
                                        }))
                                    }
                                    onChange={(value) => open ? onLocationTypeChanged(value, "modal") : true}
                                />
                            </Form.Item>
                            <Form.Item name="parentId" label={<>{parentLabel}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                <Select
                                    className="w-full"
                                    placeholder={parentLabel}
                                    filterOption={hrnUtils.dropdownFilterOption}
                                    options={
                                        parents.map((x: any) => ({
                                            label: x.name,
                                            value: x.id,
                                        }))
                                    }
                                    showSearch
                                />
                            </Form.Item>
                        </Form>
                    </div>
                </HrnModal>
            </div>
            <div>
                <Table
                    rowKey="id"
                    columns={tableUtils.buildColumns(
                        getLocationsResult.isSuccess ? dataSource : [],
                        LocationsColumn,
                        tableUtils.editDeleteActionColumns(handleEdit, handleDelete),
                        [SortingHandler],
                        undefined,
                        { serialNo: true }
                    )}
                    dataSource={dataSource}
                    size="middle"
                    className="ant_table"
                    loading={{
                        spinning: getLocationsResult.isFetching as boolean,
                        indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                    pagination={false}
                />
            </div>
        </>
    );
};

export default Location;
